/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import {capitalize, copyToClipboard, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import Invoice from '../../../models/invoiceModel';
import {PeppolState} from '../../../util/enums';
import {onInvoicePreview} from '../../../util/events';
import {
    downloadInvoice, getPeppolEnabled, getPeppolInvoiceCounts, togglePeppolEnabled
} from '../../../util/requestSender';
import {createErrorMessage} from '../../../util/utils';
import {AppContext} from '../../misc/appContext';
import PeppolInvoices from './peppolInvoices';
import {PeppolStatus} from "../../../util/types";

const defaultCounts: Record<PeppolState, number> = {
    [PeppolState.TRANSIT]: 0,
    [PeppolState.SENT]: 0,
    [PeppolState.FAILED]: 0
};
const searchDebounce = 300;

type LoadParams = {limit?: number; page?: number; search?: string | null; peppolState?: PeppolState | null;}

export default function PeppolInvoicesController() {
    const {account} = useContext(AppContext).accountContext;
    const [counts, setCounts] = useState<Record<PeppolState, number>>(defaultCounts);
    const [lastCounts, setLastCounts] = useState<Record<PeppolState, number>>(defaultCounts);
    const [loadingCounts, setLoadingCounts] = useState(true);
    const [invoices, setInvoices] = useState<Array<Invoice>>([]);
    const [peppolState, setPeppolState] = useState<PeppolState | null>(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState<number>(10);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [peppolStatus, setPeppolStatus] = useState<PeppolStatus>({hasRegistration: false, enabled: false});
    const debounce = useRef<number | undefined>(undefined);
    const navigate = useNavigate();

    function onChangeSearch(value: string) {
        clearDebounce();
        setSearch(value);
        debounce.current = window.setTimeout(() => {
            setPage(0);
            loadInvoices({search: value, page: 0});
        }, searchDebounce);
    }

    function onEnterSearch() {
        setPage(0);
        loadInvoices({page: 0});
    }

    function onChangePeppolState(peppolState: PeppolState | null) {
        return function() {
            setPeppolState(peppolState);
            setPage(0);
            loadInvoices({page: 0, peppolState});
        };
    }

    function onChangePage(nextPage: number) {
        return function() {
            setPage(nextPage);
            loadInvoices({page: nextPage});
        };
    }

    function onChangeLimit(_limit: number) {
        if (_limit === limit) return;
        setLimit(_limit);
        setPage(0);
        loadInvoices({limit: _limit, page: 0});
    }

    function onClickDetails(invoice: Invoice) {
        return function() {
            navigate(`/factuur-details?id=${invoice.id}`);
        };
    }

    function onClickPreview(invoice: Invoice) {
        return function() {
            setLoading(true);
            downloadInvoice({
                invoice_id: invoice.id,
                account_id: invoice.accountId,
                noHistory: true,
                callback: (data: ResponseData, response: Response) => {
                    setLoading(false);
                    if (data.status === 'success')
                        // FILE RESPONSE
                        response.blob().then((blob: Blob) => {
                            onInvoicePreview(URL.createObjectURL(blob));
                        });
                    else if (!data.handled)
                        showDialog(
                            createErrorMessage(data.message ?? 'Het downloaden van de factuur is niet gelukt.'),
                            'Downloaden mislukt');
                }
            });
        };
    }

    function onClickSend(invoice: Invoice) {
        return function() {
            setLoading(true);
            invoice.sendPeppol((invoice: Invoice, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    showSnackbar('De factuur is verzonden naar de klant');
                    const _invoices = invoices.slice();
                    for (let ix = 0; ix < _invoices.length; ix++) {
                        if (_invoices[ix].id === invoice.id) {
                            _invoices.splice(ix, 1, invoice);
                            setInvoices(_invoices);
                            break;
                        }
                    }
                } else showDialog(
                    data.message ?? createErrorMessage('Het verzenden van de factuur via Peppol is niet gelukt.'),
                    'Verzending mislukt');
            });
        };
    }

    function onCopyInvoiceID(invoice: Invoice) {
        return function() {
            copyToClipboard(invoice.id).then(() => {
                showSnackbar(`'${invoice.id}' gekopieerd`);
            });
        };
    }

    function onToggleEnabled() {
        if (!account) return;
        const enabled = !peppolStatus.enabled;
        setLoadingCounts(true);
        togglePeppolEnabled({
            data: {
                enabled, account_id: account.id,
            }, callback: (data: ResponseData) => {
                setLoadingCounts(false);
                if (data.success) setPeppolStatus({...peppolStatus, enabled});
                else {
                    const action = enabled ? 'inschakelen' : 'uitschakelen';
                    showDialog(createErrorMessage(data.message ?? `Het ${action} van Peppol is niet gelukt.`),
                        `${capitalize(action)} mislukt`);
                }
            }
        });
    }

    function loadInvoices(params?: LoadParams) {
        if (!account) return;
        setLoading(true);
        const _page = params?.page ?? page;
        const _peppolState = params?.peppolState === undefined ? peppolState : params.peppolState;
        Invoice.getPeppol({
            accountID: account.id, search: params?.search ?? search, page: _page, limit: params?.limit ?? limit,
            peppolState: _peppolState
        }, (invoices: Array<Invoice>, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                setInvoices(invoices);
                if (_page === 0) setTotal(data.total);
            } else showDialog(createErrorMessage(data.message ?? 'Het laden van de facturen is niet gelukt.'),
                'Facturen laden mislukt');
        });
    }

    function loadCounts() {
        if (!account) return;
        setLoadingCounts(true);
        getPeppolInvoiceCounts({
            account_id: account.id, callback: (data: ResponseData) => {
                setLoadingCounts(false);
                if (data.success) {
                    setCounts(data.counts);
                    setLastCounts(data.last_counts);
                }
            }
        });
    }

    function loadEnabled() {
        if (!account) return;
        getPeppolEnabled({
            account_id: account.id, callback: (data: ResponseData) => {
                if (data.success) setPeppolStatus(
                    {hasRegistration: data.has_registration, enabled: data.peppol_enabled});
            }
        })
    }

    function clearDebounce() {
        window.clearTimeout(debounce.current);
        debounce.current = undefined;
    }

    useEffect(() => {
        loadInvoices();
        loadCounts();
        loadEnabled();

        return function() {
            clearDebounce();
        };
    }, [account?.id]);

    return (
        <PeppolInvoices onChangeSearch={onChangeSearch} onEnterSearch={onEnterSearch}
                        onChangePeppolState={onChangePeppolState} onChangePage={onChangePage}
                        onChangeLimit={onChangeLimit} onClickDetails={onClickDetails} onClickPreview={onClickPreview}
                        onClickSend={onClickSend} onCopyInvoiceID={onCopyInvoiceID} onToggleEnabled={onToggleEnabled}
                        counts={counts} lastCounts={lastCounts} invoices={invoices} peppolState={peppolState}
                        total={total} page={page} limit={limit} search={search} loading={loading}
                        loadingCounts={loadingCounts} peppolStatus={peppolStatus}/>
    );
}